/**
 * ticketCierreAsciiLayout.ts — Rejilla térmica ESC/POS del cierre (48/32 cols).
 *
 * Celdas `+---+` y `|` (PC437), separadores `_` / `=`. Misma familia que venta/movimiento.
 * 80mm = 48 columnas · 58mm = 32 columnas (etiquetas abreviadas y bloques apilados).
 */

import {
  emitSepLine,
  gridLineSep,
  gridRowCells,
  padCol,
  truncCell,
} from "./escposLayout";

export const moneyTicket = (n: number): string =>
  `$${Math.round(n).toLocaleString("es-CO")}`;

export function ticketCols(paperWidthMm: 58 | 80): number {
  return paperWidthMm === 80 ? 48 : 32;
}

/** 58mm / 32 columnas — layout compacto. */
export function isNarrowTicket(w: number): boolean {
  return w <= 32;
}

export function labelForWidth(full: string, narrow: string, w: number): string {
  return isNarrowTicket(w) ? narrow : full;
}

export function fitCell(text: string, maxLen: number): string {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= maxLen) return t;
  if (maxLen <= 1) return t.slice(0, maxLen);
  return t.slice(0, maxLen - 1) + ".";
}

export function majorSep(w: number): string {
  return emitSepLine("=", w);
}

export function minorSep(w: number): string {
  return emitSepLine("_", w);
}

export function centered(text: string, w: number): string {
  return padCol(text, w, "center");
}

export function blank(w: number): string {
  return " ".repeat(w);
}

/** Meta «Apertura: …» alineada a derecha dentro de w cols. */
export function metaRow(label: string, value: string, w: number): string {
  const gap = Math.max(1, w - label.length - value.length);
  return `${label}${" ".repeat(gap)}${value}`.slice(0, w);
}

export function sectionMajor(lines: string[], title: string, w: number): void {
  lines.push(majorSep(w));
  for (const ln of wrapText(title, w)) {
    lines.push(centered(ln, w));
  }
  lines.push(majorSep(w));
}

export function sectionSub(lines: string[], title: string, w: number): void {
  lines.push(minorSep(w));
  for (const ln of wrapText(title, w)) {
    lines.push(centered(ln, w));
  }
  lines.push(minorSep(w));
}

/** Ancho de celdas para rejilla `|…|…|` que ocupa exactamente `w` cols. */
export function resolveThermalGridWidths(
  w: number,
  colWeights: number[],
): number[] {
  const n = colWeights.length;
  if (n === 0) return [];
  const available = Math.max(n, w - (n + 1));
  const weights = colWeights.map((c) => Math.max(1, c));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  const widths = weights.map((wt) =>
    Math.max(1, Math.floor((wt / weightSum) * available)),
  );

  let remainder = available - widths.reduce((a, b) => a + b, 0);
  let i = 0;
  while (remainder > 0) {
    widths[i % n]++;
    remainder--;
    i++;
  }
  while (remainder < 0) {
    const j = widths.findIndex((x) => x > 1);
    if (j < 0) break;
    widths[j]--;
    remainder++;
  }
  return widths;
}

function table2ColWidths(w: number): number[] {
  const right = w >= 48 ? 13 : 12;
  const left = w - right - 3;
  return [Math.max(1, left), right];
}

function thermalMoneyCells(cells: string[], widths: number[]): string[] {
  return cells.map((raw, i) => {
    if (/\$\d/.test(raw ?? "")) {
      const t = (raw ?? "").trim();
      return t.length <= widths[i]
        ? " ".repeat(widths[i] - t.length) + t
        : t.slice(0, widths[i]);
    }
    return truncCell(raw ?? "", widths[i]);
  });
}

/** Tabla 2 columnas con rejilla térmica `+---+` (ventas, caja física, ingresos…). */
export function table2Col(
  w: number,
  leftHdr: string,
  rightHdr: string,
  rows: { left: string; right: string }[],
  opts?: {
    footerRows?: { left: string; right: string }[];
    footerGroups?: { left: string; right: string }[][];
    closeBottom?: boolean;
  },
): string[] {
  const widths = table2ColWidths(w);
  const out: string[] = [];
  out.push(gridLineSep(widths));
  out.push(gridRowCells([leftHdr, rightHdr], widths));
  out.push(gridLineSep(widths));
  for (const row of rows) {
    out.push(
      gridRowCells(thermalMoneyCells([row.left, row.right], widths), widths),
    );
  }
  if (opts?.footerGroups?.length) {
    for (const group of opts.footerGroups) {
      out.push(gridLineSep(widths));
      for (const row of group) {
        out.push(
          gridRowCells(
            thermalMoneyCells([row.left, row.right], widths),
            widths,
          ),
        );
      }
    }
  } else if (opts?.footerRows?.length) {
    out.push(gridLineSep(widths));
    for (const row of opts.footerRows) {
      out.push(
        gridRowCells(thermalMoneyCells([row.left, row.right], widths), widths),
      );
    }
  }
  if (opts?.closeBottom !== false) {
    out.push(gridLineSep(widths));
  }
  return out;
}

/** Fila simple label + monto alineado a la derecha (totales fuera de tabla). */
export function totalRow(label: string, value: string, w: number): string {
  const val = value.startsWith("$") ? value : value;
  const gap = Math.max(1, w - label.length - val.length);
  return `${label}${" ".repeat(gap)}${val}`.slice(0, w);
}

/** Rejilla N columnas con cabecera, filas y pie opcional (`+---+` / `|`). */
export function gridTable(
  w: number,
  colWeights: number[],
  headers: string[],
  rows: string[][],
  footer?: string[],
  opts?: { closeBottom?: boolean },
): string[] {
  const widths = resolveThermalGridWidths(w, colWeights);
  const out: string[] = [];
  out.push(gridLineSep(widths));
  out.push(gridRowCells(headers, widths));
  out.push(gridLineSep(widths));
  for (const row of rows) {
    out.push(gridRowCells(thermalMoneyCells(row, widths), widths));
  }
  if (footer?.length) {
    out.push(gridLineSep(widths));
    out.push(gridRowCells(thermalMoneyCells(footer, widths), widths));
  }
  if (opts?.closeBottom !== false) {
    out.push(gridLineSep(widths));
  }
  return out;
}

const DENOMS = [
  100_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000, 500, 200, 100, 50,
];

function formatDenomLine(d: number, qty: number, cellW: number): string {
  const sub = moneyTicket(d * qty);
  const variants = [
    `$${d.toLocaleString("es-CO")}x${qty}:${sub}`,
    `$${d.toLocaleString("es-CO")} x${qty}: ${sub}`,
    `$${d.toLocaleString("es-CO")}x${qty}`,
  ];
  for (const v of variants) {
    if (v.length <= cellW) return v;
  }
  return fitCell(variants[0], cellW);
}

function denomLines(
  den: Record<number, number> | undefined,
  cellW: number,
): { bills: string[]; coins: string[] } {
  const bills: string[] = [];
  const coins: string[] = [];
  for (const d of DENOMS) {
    const qty = den?.[d] ?? 0;
    if (!qty) continue;
    const line = formatDenomLine(d, qty, cellW);
    if (d >= 1_000) bills.push(line);
    else coins.push(line);
  }
  return { bills, coins };
}

function billetesMonedasStacked(
  w: number,
  bills: string[],
  coins: string[],
  totalLabel: string,
  total: number,
): string[] {
  const out: string[] = [];
  out.push(centered("BILLETES", w));
  if (bills.length) {
    for (const bl of bills) {
      out.push(` ${bl}`.slice(0, w));
    }
  } else {
    out.push(blank(w));
  }
  out.push(centered("MONEDAS", w));
  if (coins.length) {
    for (const cl of coins) {
      out.push(` ${cl}`.slice(0, w));
    }
  } else {
    out.push(blank(w));
  }
  out.push(minorSep(w));
  out.push(totalRow(totalLabel, moneyTicket(total), w));
  return out;
}

/** Bloque billetes | monedas (arqueo oficial / intento fallido). */
export function billetesMonedasBlock(
  w: number,
  denominaciones: Record<number, number> | undefined,
  totalLabel: string,
  total: number,
): string[] {
  if (isNarrowTicket(w)) {
    const { bills, coins } = denomLines(denominaciones, w - 1);
    return billetesMonedasStacked(w, bills, coins, totalLabel, total);
  }

  const sep = "|";
  const colW = Math.max(8, Math.floor((w - sep.length) / 2));
  const rightW = w - sep.length - colW;
  const { bills, coins } = denomLines(denominaciones, colW);
  const maxRows = Math.max(bills.length, coins.length, 1);
  const out: string[] = [];

  out.push(padCol("Billetes", colW) + sep + padCol("Monedas", rightW));
  for (let i = 0; i < maxRows; i++) {
    out.push(
      padCol(bills[i] ?? "", colW) + sep + padCol(coins[i] ?? "", rightW),
    );
  }
  out.push(minorSep(w));
  out.push(totalRow(totalLabel, moneyTicket(total), w));
  return out;
}

/** Tarjeta propina por empleado (pipe `|` — térmica). */
export function propinaEmpleadoCard(
  w: number,
  nombre: string,
  total: number,
  porMetodo: {
    efectivo: number;
    nequi: number;
    daviplata: number;
    tarjeta: number;
  },
): string[] {
  if (isNarrowTicket(w)) {
    const out: string[] = [];
    out.push(minorSep(w));
    out.push(totalRow(nombre.toUpperCase(), moneyTicket(total), w));
    const parts: string[] = [];
    if (porMetodo.efectivo > 0)
      parts.push(`Ef:${moneyTicket(porMetodo.efectivo)}`);
    if (porMetodo.nequi > 0) parts.push(`Nq:${moneyTicket(porMetodo.nequi)}`);
    if (porMetodo.daviplata > 0)
      parts.push(`Dv:${moneyTicket(porMetodo.daviplata)}`);
    if (porMetodo.tarjeta > 0)
      parts.push(`Tj:${moneyTicket(porMetodo.tarjeta)}`);
    const metodos =
      parts.length > 0 ? parts.join(" ") : "Ef:$0 Nq:$0 Dv:$0 Tj:$0";
    for (const ln of wrapText(metodos, w - 1)) {
      out.push(` ${ln}`);
    }
    return out;
  }

  const half = Math.floor((w - 1) / 2);
  const fmt = (label: string, n: number) =>
    n > 0 ? `${label}: ${moneyTicket(n)}` : `${label}: $0`;
  const nameLine =
    `${nombre.toUpperCase()}`.slice(0, half).padEnd(half) +
    "|" +
    ` TOTAL: ${moneyTicket(total)}`.padStart(w - half - 1);
  const row2 =
    `   ${fmt("Efectivo", porMetodo.efectivo)}`.slice(0, half).padEnd(half) +
    "|" +
    fmt("Nequi", porMetodo.nequi).padStart(w - half - 1);
  const row3 =
    `   ${fmt("Daviplata", porMetodo.daviplata)}`.slice(0, half).padEnd(half) +
    "|" +
    fmt("Tarjeta", porMetodo.tarjeta).padStart(w - half - 1);
  return [
    minorSep(w),
    nameLine.slice(0, w),
    row2.slice(0, w),
    row3.slice(0, w),
    minorSep(w),
  ];
}

/** Pesos de columnas para movimientos de caja según ancho de papel. */
export function movimientoCajaCols(
  w: number,
): [number, number, number, number] {
  return isNarrowTicket(w) ? [6, 12, 5, 9] : [10, 11, 7, 20];
}

function boxedLinesBlock(detailLines: string[], w: number): string[] {
  if (!detailLines.length) return [];
  const inner = Math.max(1, w - 2);
  const out: string[] = [];
  out.push(`+${"-".repeat(inner)}+`);
  for (const raw of detailLines) {
    for (const ln of wrapText(raw, inner)) {
      out.push(`|${truncCell(ln.trim(), inner)}|`);
    }
  }
  out.push(`+${"-".repeat(inner)}+`);
  return out;
}

/** Cabecera única de la tabla de movimientos (Tipo / Monto / Hora / Resp.). */
export function movimientoCajaTableHeader(
  w: number,
  cols: [number, number, number, number],
): string[] {
  const widths = resolveThermalGridWidths(w, cols);
  return [
    gridLineSep(widths),
    gridRowCells(["Tipo", "Monto", "Hora", "Resp."], widths),
    gridLineSep(widths),
  ];
}

/** Una fila de movimiento + caja de detalle ancho completo (sin repetir cabecera). */
export function movimientoCajaEntry(
  w: number,
  cols: [number, number, number, number],
  tipo: string,
  monto: string,
  hora: string,
  resp: string,
  detalleLines: string[],
): string[] {
  const widths = resolveThermalGridWidths(w, cols);
  const out: string[] = [];
  out.push(
    gridRowCells(thermalMoneyCells([tipo, monto, hora, resp], widths), widths),
  );
  out.push(...boxedLinesBlock(detalleLines, w));
  return out;
}

export function wrapText(text: string, w: number): string[] {
  const raw = text || "";
  if (!raw.trim()) return [];
  const out: string[] = [];
  // Respetamos los saltos de línea explícitos del usuario: cada bloque se
  // envuelve por separado para que la impresión/compartir conserve el formato.
  const paragraphs = raw.split("\n");
  for (const paragraph of paragraphs) {
    const t = paragraph.replace(/\s+/g, " ").trim();
    if (!t) {
      out.push("");
      continue;
    }
    let rest = t;
    while (rest.length > w) {
      let cut = rest.lastIndexOf(" ", w);
      if (cut <= 0) cut = w;
      out.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}
